import { startOfMonth } from 'date-fns/startOfMonth';
import { FIRST_MARGIN_YEAR } from '@bloomays-lib/types.shared';
import { formatMonthYearFr } from '@bloomays-lib/utils.shared';
import {
  ActiveTam,
  AnnualTamPrimeAggregate,
  CLevelMarginRankingRow,
  LifetimeMarginCumulAggregate,
} from '../types/primes';
import { getAnnualGoalHeaderState } from './annualPrime.utils';
import { formatPrimeEuro } from './monthlyPrime.utils';

const COMPACT_MARGIN_MILLION_THRESHOLD = 1_000_000;
const COMPACT_MARGIN_THOUSAND_THRESHOLD = 1_000;

const formatCompactNumberFr = (value: number, fractionDigits: number): string =>
  new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);

/** Montant abrégé k€ / M€ pour le classement C-Level (TEC-3114). */
export const formatCompactMarginEuro = (amount: number): string => {
  const absoluteAmount = Math.abs(amount);
  if (absoluteAmount >= COMPACT_MARGIN_MILLION_THRESHOLD) {
    return `${formatCompactNumberFr(amount / COMPACT_MARGIN_MILLION_THRESHOLD, 2)} M€`;
  }
  if (absoluteAmount >= COMPACT_MARGIN_THOUSAND_THRESHOLD) {
    const fractionDigits = absoluteAmount >= 10_000 ? 0 : 1;
    return `${formatCompactNumberFr(amount / 1_000, fractionDigits)} k€`;
  }
  return formatPrimeEuro(amount);
};

/** Sous-titre KPI lifetime — « Depuis {mois année} ». */
export const formatStaffEntrySubtitle = (staffEntryDate: string | null | undefined): string => {
  if (staffEntryDate) {
    const parsed = new Date(staffEntryDate);
    if (!Number.isNaN(parsed.getTime())) {
      return formatMonthYearFr(startOfMonth(parsed));
    }
  }
  return formatMonthYearFr(new Date(FIRST_MARGIN_YEAR, 0, 1));
};

export const mapLifetimeMarginCumulToSubtitle = (cumul: LifetimeMarginCumulAggregate | null | undefined): string =>
  formatStaffEntrySubtitle(cumul?.staffEntryDate);

const compareRankingAmounts = (left: CLevelMarginRankingRow, right: CLevelMarginRankingRow): number =>
  right.amount - left.amount;

const buildTamInitials = (tam: Pick<ActiveTam, 'firstname' | 'lastname'>): string =>
  `${tam.firstname?.trim().charAt(0) ?? ''}${tam.lastname?.trim().charAt(0) ?? ''}`.toUpperCase() || '—';

export const buildAnnualMarginAmountByTam = (
  synthesisRows: Array<{ tamEmail: string; annualTamPrime?: AnnualTamPrimeAggregate }>,
): Record<string, number> =>
  synthesisRows.reduce<Record<string, number>>((amountsByTam, row) => {
    if (!row.annualTamPrime) {
      amountsByTam[row.tamEmail] = 0;
      return amountsByTam;
    }
    amountsByTam[row.tamEmail] = getAnnualGoalHeaderState(row.annualTamPrime).cumulativeMargin;
    return amountsByTam;
  }, {});

export const buildCLevelMarginRankingRows = (params: {
  activeTams: ActiveTam[];
  amountByTamEmail: Record<string, number>;
}): CLevelMarginRankingRow[] => {
  const rows = params.activeTams.map((tam) => {
    const normalizedEmail = tam.email.trim().toLowerCase();
    const amount = params.amountByTamEmail[normalizedEmail] ?? params.amountByTamEmail[tam.email] ?? 0;
    return {
      rank: 0,
      tam,
      tamInitials: buildTamInitials(tam),
      amount,
      displayAmount: formatCompactMarginEuro(amount),
      progressPercent: 0,
    };
  });

  const sortedRows = [...rows].sort(compareRankingAmounts);
  const topAmount = sortedRows[0]?.amount ?? 0;

  return sortedRows.map((row, index) => ({
    ...row,
    rank: index + 1,
    progressPercent: topAmount > 0 ? Math.round((row.amount / topAmount) * 100) : 0,
  }));
};

export const findLifetimeCumulForTam = (
  cumuls: LifetimeMarginCumulAggregate[],
  tamEmail: string,
): LifetimeMarginCumulAggregate | null => {
  const normalizedEmail = tamEmail.trim().toLowerCase();
  return cumuls.find((cumul) => cumul.tam.trim().toLowerCase() === normalizedEmail) ?? null;
};

export const buildLifetimeAmountByTam = (cumuls: LifetimeMarginCumulAggregate[]): Record<string, number> =>
  cumuls.reduce<Record<string, number>>((amountsByTam, cumul) => {
    amountsByTam[cumul.tam.trim().toLowerCase()] = cumul.totalMargin;
    return amountsByTam;
  }, {});

export const buildLifetimeDisplayAmountByTam = (cumuls: LifetimeMarginCumulAggregate[]): Record<string, string> =>
  cumuls.reduce<Record<string, string>>((amountsByTam, cumul) => {
    amountsByTam[cumul.tam.trim().toLowerCase()] = formatCompactMarginEuro(cumul.totalMargin);
    return amountsByTam;
  }, {});
